import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { supabase } from '@/integrations/supabase/client';
import { isNative } from '@/lib/platform';

/**
 * Hook to handle deep links when the app is opened via a URL.
 * This is used for email confirmation links that contain auth tokens.
 * 
 * When a user clicks a confirmation email link on their phone:
 * 1. iOS/Android intercepts the URL (via Universal Links / App Links)
 * 2. The native app opens and receives the URL via appUrlOpen event
 * 3. This hook extracts the auth tokens and logs the user in
 */
export function useDeepLinks() {
  const navigate = useNavigate();

  useEffect(() => {
    // Only set up deep link handling on native platforms
    if (!isNative()) {
      return;
    }

    const handleAppUrlOpen = async (event: URLOpenListenerEvent) => {
      console.log('[DeepLinks] App opened with URL:', event.url);

      try {
        const url = new URL(event.url);
        
        // Check for auth tokens in the URL fragment (Supabase format)
        // Example: https://lekalearning.com/translate#access_token=xxx&refresh_token=yyy&...
        const fragment = url.hash.substring(1); // Remove leading #
        
        if (fragment) {
          const params = new URLSearchParams(fragment);
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');

          if (accessToken && refreshToken) {
            console.log('[DeepLinks] Found auth tokens, setting session...');
            
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (error) {
              console.error('[DeepLinks] Error setting session:', error);
            } else {
              console.log('[DeepLinks] Session set successfully');
            }
          }
        }

        // Navigate to the intended path
        const pathname = url.pathname;
        const search = url.search;
        
        // Navigate to the path from the URL
        if (pathname && pathname !== '/') {
          console.log('[DeepLinks] Navigating to:', pathname + search);
          navigate(pathname + search, { replace: true });
        } else {
          // Default to translate page if no specific path
          navigate('/translate', { replace: true });
        }
      } catch (error) {
        console.error('[DeepLinks] Error handling URL:', error);
        // Fallback to translate page on error
        navigate('/translate', { replace: true });
      }
    };

    // Add listener for app URL open events
    const listenerPromise = App.addListener('appUrlOpen', handleAppUrlOpen);

    // Cleanup listener on unmount
    return () => {
      listenerPromise.then(listener => listener.remove());
    };
  }, [navigate]);
}
